import React from 'react';
import { inject, observer } from 'mobx-react';
import classnames from 'classnames';

import { cdnFile } from '@/utils/cdnFile';
import { handleRequest } from '@/utils';
import { addDialogue, updateDialogueInfo } from '@/utils/sessionUtils';
import { LOCK_INTENT_NEXT_PAGE_TIME, TRACKING_TYPE, HotQuestionCategoryDisplayType } from '@/utils/constants';
import { InitStoreType } from '@/types/store/init';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import { MAX_HOT_QUESTION_NUMBER } from '@/components/IntentHotQuestion/constants';
import { getChildInject, isNum } from '@/utils/instantLoaded/tools';
import sendBotMessageToAgent from '@/utils/sendBotMessageToAgent';
import sessionStore from '@/stores/session';
import IntentIcon from '../IntentIcon';

import './style.scss';

const PREFIX = 'category-item';

export interface CategoryProps {
  id: number;
  label: string;
  icon: string;
  isAdHoc: boolean;
  color: string;
  index: number;
  initStore?: InitStoreType;
  agentStore?: any;
  firstInit?: any;
  isImg: boolean;
  displayType?: number;
  imgIconSrc?: string;
  loading: boolean;
}

@inject(getChildInject('firstInit', 'initStore', 'agentStore', 'sessionStore'))
@observer
export default class Category extends React.Component<CategoryProps> {
  trackingname;
  trackingdata = {};
  categoryRef = React.createRef<HTMLDivElement>();

  // 获取下一页intent列表，如果为true，表示暂时不可以获取
  // 当前前端设置，1s后才可以进行下一次获取
  isNextIntentLock = false;

  constructor(props: CategoryProps) {
    super(props);

    this.trackingname = TRACKING_TYPE.CATEGORY_OPTIMISATION;
    this.trackingdata = {
      chat_session_id: props.initStore?.sessionId || '',
      question_category: props.label,
      location: props.index,
    };
  }

  componentDidMount() {
    addObserver(this.categoryRef.current);
  }

  componentWillUnmount() {
    removeObserver(this.categoryRef.current);
  }

  handleCategoryClick = async () => {
    const { label, id, initStore, agentStore, firstInit } = this.props;

    import('@/utils/btnClickHandler').then(({ trackingHandler }) => {
      trackingHandler({
        trackingname: this.trackingname,
        trackingdata: this.trackingdata,
      });
    });

    // 在agent过程中，走agent逻辑
    if (agentStore.inAgent) {
      // [live-chat-split-change]
      sendBotMessageToAgent({ content: label });
    } else {
      if (this.isNextIntentLock) {
        return;
      }

      const curPos = addDialogue({
        from: 'user',
        type: 'Msg',
        data: { content: label },
      });

      if (isNum(curPos)) {
        const params = {
          category_id: id,
          session_id: initStore!.sessionId,
        };

        this.isNextIntentLock = true;

        firstInit.UPDATE_FIRST_LOAD_LOADING(true);

        const reporter = (await import('@/utils/report')).default;
        reporter.apiBatchReport({ type: 'count', value: { label: 'func_head_hqs_load' }});

        handleRequest('getCategoryIntentList', params, { curPos, firstScreen: true }).then(async (res: any) => {
          res
            && updateDialogueInfo(
              {
                dialogueId: res.dialogue_id,
                dialogueType: res.dialogue_type,
              },
              curPos,
            );
          if (res) {
            const { refresh_head_intents: hotQuestions = [], refresh_shortcut_intents: shortcutIntent = [] }
              = res || {};

            if (hotQuestions?.length === MAX_HOT_QUESTION_NUMBER || !hotQuestions?.length) {
              reporter.apiBatchReport({ type: 'count', value: { label: 'func_head_hqs_load_ok' }});
            }

            if (hotQuestions?.length) {
              sessionStore.UPDATE_INTENT_HOT_QUESTION(hotQuestions);
            }

            const { shortcutPredictionAvaliable } = await import('@/utils/tools');
            if (shortcutPredictionAvaliable && shortcutIntent?.length) {
              this.props.initStore?.UPDATE_INTENT_PREDICTION_SHORTCUT_IF_ALLOWED(shortcutIntent);
            }
          }
        });
        setTimeout(() => {
          this.isNextIntentLock = false;
        }, LOCK_INTENT_NEXT_PAGE_TIME);
      }
    }
  };

  render() {
    const { icon, label, color, index, isImg, id, displayType, imgIconSrc, loading } = this.props;
    const isOriginIntentHotQuestion = this.props?.displayType === HotQuestionCategoryDisplayType.Old;

    return (
      <div
        ref={this.categoryRef}
        className={classnames(PREFIX, { 'ad-hoc': isImg }, 'category-item-group')}
        onClick={this.handleCategoryClick}
        data-trackingname={TRACKING_TYPE.CATEGORY_OPTIMISATION}
        data-trackingdata={JSON.stringify(this.trackingdata)}
        data-trackingindex={index}
      >
        {isImg ? (
          <div className={`${PREFIX}-icon`}>
            {!loading ? <img className="icon-img" src={imgIconSrc || cdnFile(icon)} /> : null}
          </div>
        ) : (
          <div className={`${PREFIX}-icon-svg`}>
            {!loading ? (
              <IntentIcon iconName={icon} color={color} id={id} displayType={displayType} imgIconSrc={imgIconSrc} />
            ) : null}
          </div>
        )}
        <div className={classnames(`${PREFIX}-label`, { 'ad-hoc': isImg && isOriginIntentHotQuestion })}>{label}</div>
      </div>
    );
  }
}
